import React, { useState } from 'react';
import { TableroKanban } from './TableroKanban';
import { VistaTabla } from './VistaTabla';
import { VistaCalendario } from './VistaCalendario';
import { VistaTimeline } from './VistaTimeline';
import { VistaPorCliente } from './VistaPorCliente';
import { SelectorVista, useVistaActiva } from './SelectorVista';
import { CFDChart } from './CFDChart';
import { ThroughputHistoryChart } from './ThroughputHistoryChart';
import { CuellosBotellaPanel } from './CuellosBotellaPanel';
import { StandupPanel } from './StandupPanel';
import { RetrospectivaPanel } from './RetrospectivaPanel';
import { AlertasPanel } from './AlertasPanel';
import { ReplenishSuggestionPanel } from './ReplenishSuggestionPanel';

type SubTab = 'vista' | 'por_cliente' | 'metricas' | 'cuellos' | 'standup' | 'retrospectiva' | 'alertas' | 'replenish';

const SUB_TABS: Array<{ key: SubTab; label: string; icon: string }> = [
    { key: 'vista',         label: 'Vista General',      icon: '📋' },
    { key: 'por_cliente',   label: 'Por Cliente',        icon: '👥' },
    { key: 'metricas',      label: 'Métricas',           icon: '📊' },
    { key: 'cuellos',       label: 'Cuellos de Botella', icon: '🔍' },
    { key: 'standup',       label: 'Standup',            icon: '📅' },
    { key: 'retrospectiva', label: 'Retrospectiva',      icon: '🔄' },
    { key: 'alertas',       label: 'Alertas',            icon: '🚨' },
    { key: 'replenish',     label: 'Reposición',         icon: '📥' },
];

// Antes eran 8 pestañas de nivel superior — en el celular obligaba a
// scrollear la barra de navegación entera para llegar a cualquiera de
// ellas. Se agrupan bajo un solo tab "Tablero" (mismo criterio que
// VistaVentas.tsx) con esta sub-navegación adentro. flex-wrap en vez de
// scroll horizontal: con etiquetas cortas entran en 2-3 líneas sin tener
// que deslizar el dedo para encontrar la que se busca.
export const VistaTablero: React.FC = () => {
    const [subTab, setSubTab] = useState<SubTab>('vista');
    const [vistaActiva, setVistaActiva] = useVistaActiva();

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-1 border-b border-slate-200">
                {SUB_TABS.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setSubTab(tab.key)}
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                            subTab === tab.key
                                ? 'border-slate-800 text-slate-900'
                                : 'border-transparent text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        <span className="mr-1">{tab.icon}</span>{tab.label}
                    </button>
                ))}
            </div>

            {subTab === 'vista' && (
                <>
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Tablero</h2>
                            <p className="text-sm text-slate-500">Cambiá la vista según lo que necesites ver</p>
                        </div>
                        <SelectorVista activa={vistaActiva} onChange={setVistaActiva} />
                    </div>
                    {vistaActiva === 'kanban' && <TableroKanban />}
                    {vistaActiva === 'tabla' && <VistaTabla />}
                    {vistaActiva === 'calendario' && <VistaCalendario />}
                    {vistaActiva === 'timeline' && <VistaTimeline />}
                </>
            )}
            {subTab === 'por_cliente' && (
                <>
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Por Cliente</h2>
                        <p className="text-sm text-slate-500">Vista enfocada: 6 columnas con acciones rápidas (✓ completar, + agregar)</p>
                    </div>
                    <VistaPorCliente />
                </>
            )}
            {subTab === 'metricas' && (
                <div className="space-y-6">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Métricas y Visualizaciones</h2>
                        <p className="text-sm text-slate-500">CFD y throughput histórico del sistema.</p>
                    </div>
                    <CFDChart />
                    <ThroughputHistoryChart />
                </div>
            )}
            {subTab === 'cuellos' && (
                <div className="space-y-6">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Cuellos de Botella</h2>
                        <p className="text-sm text-slate-500">Items ordenados por antigüedad en columna. Bloqueados primero.</p>
                    </div>
                    <CuellosBotellaPanel />
                </div>
            )}
            {subTab === 'standup' && (
                <div className="space-y-6">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Standup Diario</h2>
                        <p className="text-sm text-slate-500">Recorrido derecha → izquierda. ¿Qué impide que fluya?</p>
                    </div>
                    <StandupPanel />
                </div>
            )}
            {subTab === 'retrospectiva' && (
                <div className="space-y-6">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Retrospectiva</h2>
                        <p className="text-sm text-slate-500">Datos agregados del período para reflexionar.</p>
                    </div>
                    <RetrospectivaPanel />
                </div>
            )}
            {subTab === 'alertas' && (
                <div className="space-y-6">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Alertas</h2>
                        <p className="text-sm text-slate-500">Lo que requiere atención inmediata.</p>
                    </div>
                    <AlertasPanel />
                </div>
            )}
            {subTab === 'replenish' && (
                <div className="space-y-6">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Reposición</h2>
                        <p className="text-sm text-slate-500">Sugeridos del Backlog para llenar Por Hacer.</p>
                    </div>
                    <ReplenishSuggestionPanel />
                </div>
            )}
        </div>
    );
};
